/**
 * Phase 4 Day 8 — Editorial section for V2CardDrawer.
 *
 * Spec ref: PHASE_4_EDITORIAL_IMPLEMENTATION_SPEC.md § 4.
 *
 * Four controls grouped in a collapsible "Editorial" section:
 *
 *  1. editorial-register-select         — dropdown (5 + None)
 *  2. editorial-register-topic-input    — conditional on ON_TOPIC
 *  3. editorial-accents-toggle          — master switch
 *  4. editorial-accent-word-preview     — BIDIRECTIONAL: clicking a
 *     word in the preview toggles **...** markers in the underlying
 *     title field. Only ONE word can be accented at a time
 *     (spec § 3.3 first-pair-only rule). Headline field is the source
 *     of truth; preview is a derived view that mutates the field on
 *     click. Both directions debounce to avoid feedback loops.
 *
 * Save behaviour: all four controls accumulate in a single PATCH at
 * Save-time. The Save button disables on validation error (topic
 * field-side validation mirrors spec § 2.1 — API layer is authoritative).
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";

const REGISTERS = [
  { value: "", label: "None (no kicker)" },
  { value: "FIELD_NOTE", label: "FIELD NOTE" },
  { value: "DIAGNOSTIC", label: "DIAGNOSTIC" },
  { value: "REPORT", label: "REPORT" },
  { value: "BRIEFING", label: "BRIEFING" },
  { value: "ON_TOPIC", label: "ON [TOPIC]" },
];

// Mirror of services/editorial_topic_validator.py rules. UI-side check
// is a UX nicety only — the API is the source of truth.
const TOPIC_CHAR_RE = /^[A-Za-z \-&']*$/;
const TOPIC_MAX_CHARS = 64;
const TOPIC_MAX_WORDS = 4;

function validateTopic(topic, register) {
  if (!topic || !topic.trim()) {
    if (register === "ON_TOPIC") return "Topic is required for ON_TOPIC register.";
    return null;
  }
  if (register !== "ON_TOPIC") {
    return "Topic only valid when register is ON_TOPIC.";
  }
  const s = topic.trim();
  if (!TOPIC_CHAR_RE.test(s)) {
    return "Allowed characters: letters, spaces, '-', '&', \"'\".";
  }
  if (s.length > TOPIC_MAX_CHARS) return `Max ${TOPIC_MAX_CHARS} characters.`;
  if (s.split(/\s+/).length > TOPIC_MAX_WORDS) return `Max ${TOPIC_MAX_WORDS} words.`;
  return null;
}

/**
 * Find the first **word** marker in the headline. Returns
 * { start, end, text } or null. ``text`` is the inner content; start/end
 * are byte offsets in the source string covering the ** ** delimiters.
 */
function findFirstAccent(headline) {
  if (!headline) return null;
  const m = /\*\*([^*\n]+?)\*\*/.exec(headline);
  if (!m) return null;
  return { start: m.index, end: m.index + m[0].length, text: m[1] };
}

/**
 * Toggle accent marker around a word at the given index. If the word
 * is already inside an accent pair, strip it. If the word is plain,
 * wrap it AND strip any other accent pair (only one accent per
 * headline, spec § 3.3).
 */
function toggleAccentAtIndex(headline, wordIndex) {
  // Re-tokenise the plain (markerless) version so wordIndex is stable
  // against the operator's previous accent choice.
  const plain = headline.replace(/\*\*([^*\n]+?)\*\*/g, "$1");
  const words = plain.split(/(\s+)/);  // keep separators so we can rebuild
  // Map word-positions to indices in the words array.
  const wordPositions = [];
  words.forEach((tok, i) => {
    if (!/^\s+$/.test(tok) && tok.length > 0) wordPositions.push(i);
  });
  if (wordIndex < 0 || wordIndex >= wordPositions.length) return headline;

  // Check current state: is THIS word the accented one?
  const currentAccent = findFirstAccent(headline);
  let currentAccentWordIndex = -1;
  if (currentAccent) {
    // Find which plain-word position the current accent maps to.
    const head = headline.slice(0, currentAccent.start).replace(/\*\*([^*\n]+?)\*\*/g, "$1");
    const leading = head.split(/(\s+)/);
    let plainBeforeCount = 0;
    leading.forEach((tok) => {
      if (!/^\s+$/.test(tok) && tok.length > 0) plainBeforeCount += 1;
    });
    currentAccentWordIndex = plainBeforeCount;
  }

  // If clicking the same word that's already accented → strip.
  if (currentAccentWordIndex === wordIndex) {
    return plain;
  }

  // Otherwise: wrap THIS word in **...** (and drop the previous pair).
  const targetTokenIdx = wordPositions[wordIndex];
  const rebuilt = words.map((tok, i) => (i === targetTokenIdx ? `**${tok}**` : tok)).join("");
  return rebuilt;
}

export default function EditorialSection({ card, workspaceId, cardId, onMutated, setCard, API }) {
  const [register, setRegister] = useState(card?.editorial_register || "");
  const [topic, setTopic] = useState(card?.editorial_register_topic || "");
  const [accentsEnabled, setAccentsEnabled] = useState(
    card?.editorial_accents_enabled !== false,
  );
  const [titleDraft, setTitleDraft] = useState(card?.title || "");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  // Reset local state whenever the card changes underneath us (version
  // bump, reload, etc.). Debounces the bidirectional preview feedback
  // loop: when the API responds with a new card.version, we accept the
  // server's version of the title rather than fighting it.
  useEffect(() => {
    setRegister(card?.editorial_register || "");
    setTopic(card?.editorial_register_topic || "");
    setAccentsEnabled(card?.editorial_accents_enabled !== false);
    setTitleDraft(card?.title || "");
  }, [card?.card_id, card?.version]);

  const topicError = useMemo(
    () => validateTopic(topic, register),
    [topic, register],
  );
  const dirty = useMemo(() => {
    return (
      (register || "") !== (card?.editorial_register || "")
      || (topic || "") !== (card?.editorial_register_topic || "")
      || accentsEnabled !== (card?.editorial_accents_enabled !== false)
      || titleDraft !== (card?.title || "")
    );
  }, [register, topic, accentsEnabled, titleDraft, card]);

  // ── Bidirectional accent-word preview ─────────────────────────────
  // Render the title as clickable word chips. The CURRENT accent (if any)
  // gets the brand-blue chip styling; other words get the muted styling.
  const accent = useMemo(() => findFirstAccent(titleDraft), [titleDraft]);
  const wordChips = useMemo(() => {
    const plain = (titleDraft || "").replace(/\*\*([^*\n]+?)\*\*/g, "$1");
    const tokens = plain.split(/(\s+)/);
    const accentText = accent?.text || null;
    let wordIdx = -1;
    let alreadyMatchedAccent = false;
    return tokens.map((tok, i) => {
      const isSpace = /^\s+$/.test(tok) || tok.length === 0;
      if (isSpace) return { kind: "space", text: tok, key: `s-${i}` };
      wordIdx += 1;
      const isAccent = !alreadyMatchedAccent && accentText !== null && tok === accentText;
      if (isAccent) alreadyMatchedAccent = true;
      return { kind: "word", text: tok, key: `w-${i}`, wordIdx, isAccent };
    });
  }, [titleDraft, accent]);

  const handleWordClick = useCallback((wordIdx) => {
    setTitleDraft((cur) => toggleAccentAtIndex(cur, wordIdx));
  }, []);

  // ── Save ──────────────────────────────────────────────────────────
  const save = async () => {
    if (topicError) {
      toast.error(topicError);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        editorial_register: register || null,
        editorial_register_topic: register === "ON_TOPIC" ? (topic || null) : null,
        editorial_accents_enabled: accentsEnabled,
        title: titleDraft,
        expected_version: card?.version,
      };
      const r = await fetch(
        `${API}/v2/workspaces/${workspaceId}/cards/${cardId}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `Save failed (${r.status})`);
      const updated = body?.card || body;
      setCard((prev) => (prev ? { ...prev, ...updated } : prev));
      toast.success("Editorial saved");
      onMutated && onMutated();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 border border-slate-200 rounded-md p-3" data-testid="editorial-section">
      <button
        type="button"
        className="w-full flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-700"
        onClick={() => setOpen((x) => !x)}
        data-testid="editorial-section-toggle"
      >
        <span>Editorial</span>
        <span className="text-slate-400">{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div className="space-y-3 pt-1">
          {/* Register dropdown */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Register
            </label>
            <select
              value={register}
              onChange={(e) => setRegister(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
              data-testid="editorial-register-select"
            >
              {REGISTERS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Topic input — conditional */}
          {register === "ON_TOPIC" && (
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                Topic
              </label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. LEADERSHIP, BOARDROOM DYNAMICS"
                className={`w-full text-sm border rounded-md px-2 py-1.5 bg-white ${topicError ? "border-rose-400" : "border-slate-300"}`}
                data-testid="editorial-register-topic-input"
              />
              {topicError && (
                <p
                  className="text-[11px] text-rose-600 mt-1"
                  data-testid="editorial-register-topic-error"
                >
                  {topicError}
                </p>
              )}
              <p className="text-[10px] text-slate-500 mt-1">
                Allowed: letters, spaces, '-', '&', "'". Max 4 words, 64 chars.
              </p>
            </div>
          )}

          {/* Accents toggle */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1">
              <label className="block text-[11px] font-semibold text-slate-600">
                Use editorial accents
              </label>
              <p className="text-[10px] text-slate-500 leading-tight">
                Brand-blue kicker, hairline, and accent word inside the headline.
                Turn off for plain layouts.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAccentsEnabled((x) => !x)}
              className={`w-10 h-6 rounded-full transition-colors ${accentsEnabled ? "bg-sky-600" : "bg-slate-300"}`}
              data-testid="editorial-accents-toggle"
              aria-pressed={accentsEnabled}
            >
              <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${accentsEnabled ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </div>

          {/* Title field (the editorial headline source) */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              Headline (title)
            </label>
            <input
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white font-mono"
              data-testid="editorial-headline-input"
              placeholder="Headline text — mark one word with **word** to accent."
            />
          </div>

          {/* Accent-word preview — bidirectional click-to-mark */}
          <div
            className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-3"
            data-testid="editorial-accent-word-preview"
          >
            {titleDraft.trim() === "" ? (
              <p className="text-[11px] text-slate-500 italic">
                Enter a headline above to preview accent placement.
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">
                  Click a word to mark it as the accent
                </p>
                <div className="text-base leading-snug">
                  {wordChips.map((c) => {
                    if (c.kind === "space") return <span key={c.key}>{c.text}</span>;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => handleWordClick(c.wordIdx)}
                        className={
                          c.isAccent
                            ? "text-sky-700 font-semibold underline decoration-2 underline-offset-2 hover:bg-sky-100 rounded px-0.5 transition-colors"
                            : "text-slate-900 hover:bg-sky-50 hover:text-sky-700 rounded px-0.5 transition-colors"
                        }
                        data-testid={`editorial-preview-word-${c.wordIdx}`}
                      >
                        {c.text}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-500">
                  {accent
                    ? `Accent: "${accent.text}". Click again to remove.`
                    : "No accent set. Click a word above (or type **word** in the headline)."}
                </p>
              </div>
            )}
          </div>

          {dirty && (
            <div className="flex justify-end pt-1">
              <Button
                size="sm"
                onClick={save}
                disabled={saving || !!topicError}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                data-testid="editorial-save-btn"
              >
                {saving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <><Save className="h-3 w-3 mr-1" />Save editorial</>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
